/**
 * L'attivazione su un telefono: una colonna sola, e il codice prima di tutto il resto.
 *
 * ## Perché non bastava rendere elastica quella del televisore
 *
 * `PairingScreen` è disegnata a `layout.designWidth` — una misura **fissa**, 1920 — con due colonne
 * affiancate e caratteri da 92px. Su uno schermo da 360 punti non si adatta male: non si adatta
 * affatto, e il risultato visto sull'emulatore era «Activate this devic» tagliato a metà parola,
 * con metà interfaccia fuori dai bordi. È la prima schermata che esiste, quindi è anche l'unica
 * cosa che un cliente col telefono riuscirebbe a vedere.
 *
 * ## L'ordine è diverso, non è la stessa roba impilata
 *
 * Sul televisore il QR sta al centro e il codice gli fa da ripiego. Qui è rovesciato: **il codice a
 * caratteri viene prima**, perché il gesto è leggerlo al telefono a un rivenditore, e il QR del
 * proprio schermo non lo inquadra nessuno. Il QR resta, più piccolo, per quando il rivenditore ha
 * il cliente davanti.
 *
 * I tre passi scendono sotto il codice invece di stare sopra: servono a chi non ha ancora capito
 * cosa fare, e chi l'ha capito vuole il codice senza scorrere.
 *
 * ## Niente marchio dell'operatore, qui come di là
 *
 * Prima dell'accoppiamento l'app non ha parlato con nessun gateway: non ha nome, logo né palette
 * (01-ARCHITETTURA §5). Vale per tutte e due le versioni della schermata.
 *
 * ## Non conta il tempo da sé
 *
 * `remainingSeconds` arriva da chi la ospita. I televisori hanno l'orologio sbagliato, spesso di
 * anni — e un telefono in viaggio cambia fuso: un conto alla rovescia locale mostra «ancora 40
 * secondi» su una richiesta già morta.
 */

import { type ReactNode } from 'react';
import { formatPairingCode, tailOf, type GatewayError } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { QrCode } from '../components/QrCode.js';
import type { PairingStatus } from '../screens/PairingScreen.js';

/**
 * Il QR, in punti.
 *
 * Più piccolo che sul televisore e non per far stare le cose: un QR che il rivenditore inquadra da
 * mezzo metro non ha bisogno di essere grande, e occupare mezzo schermo spingerebbe il codice —
 * che è la strada che si usa davvero — sotto il bordo.
 */
const QR_SIZE_PX = 184;

/** Sotto questa soglia il conto alla rovescia diventa rosso. Come sul televisore. */
const URGENT_SECONDS = 15;

export interface PhonePairingScreenProps {
  readonly deviceId: string;
  readonly appVersion: string;
  readonly status: PairingStatus;
  /** Il rifiuto della linea precedente: si mostra **insieme** al codice nuovo, non al posto suo. */
  readonly notice?: GatewayError | null;
  /** Le credenziali sono arrivate: il codice sparisce, perché è già stato consumato. */
  readonly entering?: boolean;
  readonly onRenew: () => void;
}

export function PhonePairingScreen({
  deviceId,
  appVersion,
  status,
  notice = null,
  entering = false,
  onRenew,
}: PhonePairingScreenProps): ReactNode {
  const translator = useTranslator();

  const problem: GatewayError | null = notice ?? (status.kind === 'failed' ? status.error : null);

  return (
    <div
      data-phone-pairing="1"
      style={{
        /*
          `100dvh` e non `100vh`: scorrendo, la barra degli indirizzi si ritira e `vh` resta fermo
          all'altezza massima, lasciando l'ultima riga sotto il bordo proprio all'apertura.
        */
        minHeight: '100dvh',
        boxSizing: 'border-box',
        /*
          Il riquadro scorre tutto, al contrario del guscio a tre fasce: qui non c'è una barra da
          tenere ferma, e con la tastiera aperta o un carattere di sistema grande il contenuto deve
          poter uscire di sotto invece di comprimersi.
        */
        overflowY: 'auto',
        background: colors.background,
        color: colors.text,
        padding: 'max(24px, env(safe-area-inset-top)) 20px max(24px, env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
      }}
    >
      {/* Una colonna stretta e centrata: su un telefono molto largo il testo non si spalma. */}
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 5,
            font: `800 34px/1 ${FONT_STACK}`,
            letterSpacing: '-.04em',
          }}
        >
          {translator.t('common.appName')}
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 5,
              background: colors.accent,
              display: 'inline-block',
            }}
          />
        </div>

        <div
          style={{
            font: `700 23px/1.2 ${FONT_STACK}`,
            letterSpacing: '-.02em',
            textAlign: 'center',
          }}
        >
          {translator.t('pairing.title')}
        </div>
        <div
          style={{
            font: `400 15px/1.45 ${FONT_STACK}`,
            color: colors.textMuted,
            textAlign: 'center',
          }}
        >
          {translator.t('pairing.subtitle')}
        </div>

        {entering ? (
          <div style={{ font: `600 17px ${FONT_STACK}`, color: colors.textMuted, padding: '28px 0' }}>
            {translator.t('pairing.entering')}
          </div>
        ) : status.kind === 'showing' ? (
          <>
            {/*
              Il codice, per primo e grande quanto lo schermo consente.

              `min(…vw, …px)` invece di una misura fissa: il codice è lungo e in spaziatura larga,
              e su uno schermo da 320 punti una misura fissa lo manderebbe a capo in mezzo — cioè
              lo renderebbe illeggibile proprio mentre qualcuno lo detta al telefono.
            */}
            <div
              data-testid="pairing-code"
              style={{
                font: `800 min(10vw, 40px)/1.1 ui-monospace, Menlo, monospace`,
                letterSpacing: '0.1em',
                color: colors.text,
                textAlign: 'center',
                wordBreak: 'keep-all',
              }}
            >
              {formatPairingCode(status.code)}
            </div>

            <div
              data-testid="pairing-countdown"
              style={{
                font: `600 16px ${FONT_STACK}`,
                color: status.remainingSeconds <= URGENT_SECONDS ? colors.live : colors.textMuted,
              }}
            >
              {translator.t('pairing.expiresIn', { count: status.remainingSeconds })}
            </div>

            <QrCode text={status.code} sizePx={QR_SIZE_PX} />

            <div
              style={{
                font: `400 14px/1.5 ${FONT_STACK}`,
                color: colors.textFaint,
                textAlign: 'center',
              }}
            >
              {translator.t('pairing.scanHint')}
            </div>
          </>
        ) : status.kind === 'requesting' ? (
          <div style={{ font: `600 17px ${FONT_STACK}`, color: colors.textMuted, padding: '28px 0' }}>
            {translator.t('pairing.requesting')}
          </div>
        ) : (
          /* Scaduta o fallita: un tasto solo, e **non si interroga più**. */
          <>
            <div
              style={{
                font: `600 17px/1.45 ${FONT_STACK}`,
                color: colors.textMuted,
                textAlign: 'center',
                padding: '12px 0',
              }}
            >
              {translator.t(status.kind === 'expired' ? 'pairing.expired' : 'pairing.failedBody')}
            </div>
            <button
              type="button"
              data-testid="pairing-renew"
              onClick={onRenew}
              style={{
                /*
                  48 punti: la misura sotto la quale un dito sbaglia bersaglio. Il tasto è largo
                  quanto la colonna perché è l'unica azione della schermata — non c'è niente di
                  adiacente da colpire per sbaglio.
                */
                minHeight: 48,
                width: '100%',
                padding: '0 24px',
                borderRadius: 24,
                border: `2px solid ${colors.accent}`,
                background: colors.accent,
                color: colors.background,
                font: `700 16px ${FONT_STACK}`,
                cursor: 'pointer',
              }}
            >
              {translator.t('pairing.renew')}
            </button>
          </>
        )}

        {/* I tre passi, sotto il codice: servono a chi non sa cosa fare, non a chi lo sa già. */}
        <ol
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            paddingTop: 6,
            margin: 0,
            listStyle: 'none',
          }}
        >
          {(['pairing.steps.call', 'pairing.steps.read', 'pairing.steps.wait'] as const).map(
            (key, index) => (
              <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  style={{
                    flex: 'none',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: `2px solid ${colors.border}`,
                    color: colors.textFaint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    font: `800 13px ${FONT_STACK}`,
                  }}
                >
                  {index + 1}
                </span>
                <span style={{ font: `500 15px/1.35 ${FONT_STACK}`, color: colors.textMuted }}>
                  {translator.t(key)}
                </span>
              </li>
            ),
          )}
        </ol>

        {problem !== null ? (
          <div
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: colors.errorPanel,
              border: `1px solid ${colors.live}`,
              borderRadius: 12,
              padding: '14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
            }}
          >
            <span style={{ font: `700 15px ${FONT_STACK}`, color: colors.errorText }}>
              {translator.t(notice !== null ? 'pairing.noticeTitle' : 'pairing.errorTitle')}
            </span>
            <span style={{ font: `500 15px/1.4 ${FONT_STACK}` }}>
              {translator.t(`errors.${problem.code}`, problem.params)}
            </span>
            {/* Il codice e l'ora: non per chi guarda, per chi risponde al telefono. */}
            <span style={{ font: '500 12px ui-monospace, Menlo, monospace', color: colors.textFaint }}>
              {translator.t('common.errorCodeAt', {
                code: problem.code,
                time: translator.time(problem.atMs),
              })}
            </span>
          </div>
        ) : null}

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 6,
            font: `500 12px/1.5 ${FONT_STACK}`,
            color: colors.textFaint,
            textAlign: 'center',
          }}
        >
          {translator.t('device.line', { deviceId: tailOf(deviceId, 9), version: appVersion })}
        </div>
      </div>
    </div>
  );
}
